import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Gift, XCircle, X as XIcon } from "lucide-react";
import { useAuth } from "../Context/AuthContext";

const API_BASE = "https://apisellhub.com/api";

const AssignPlanDialog = ({ open, seller, onClose, onSuccess }) => {
    const { getToken } = useAuth();

    const [plans, setPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [selectedPlanId, setSelectedPlanId] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const currentPlanId = seller?.pricing_plan_id ?? seller?.pricing_plan?.id ?? null;

    useEffect(() => {
        if (!open) return;

        const fetchPlans = async () => {
            setLoadingPlans(true);
            try {
                const token = getToken();
                const response = await axios.get(`${API_BASE}/super-admin/pricing-plans`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });

                const list = response.data?.data ?? response.data ?? [];
                setPlans(list.filter((p) => p.status === true || p.status === 1));
            } catch (err) {
                console.error("Fetch plans error:", err);
                toast.error("Failed to load plans");
            } finally {
                setLoadingPlans(false);
            }
        };

        fetchPlans();
        setSelectedPlanId(currentPlanId ? String(currentPlanId) : "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, seller?.id]);

    const submitAssign = async (planId) => {
        if (!seller?.id) return;
        setSubmitting(true);
        try {
            const token = getToken();
            const response = await axios.put(
                `${API_BASE}/super-admin/sellers-pricing-plan/${seller.id}`,
                { pricing_plan_id: planId === null ? null : Number(planId) },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data?.status !== false) {
                toast.success(planId === null ? "Plan removed" : "Plan assigned");
                onSuccess?.();
            } else {
                toast.error(response.data?.message ?? "Failed to update plan");
            }
        } catch (err) {
            console.error("Assign plan error:", err);
            toast.error(err.response?.data?.message ?? "Failed to update plan");
        } finally {
            setSubmitting(false);
        }
    };

    const handleAssign = () => {
        if (!selectedPlanId) {
            toast.error("Please select a plan");
            return;
        }
        submitAssign(selectedPlanId);
    };

    const handleRemove = () => submitAssign(null);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/60"
                onClick={() => !submitting && onClose?.()}
            />

            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Gift className="h-5 w-5 text-blue-600" />
                        Seller Pricing Plan
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"
                    >
                        <XIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-6 py-5">
                    <p className="text-sm text-gray-700">
                        Seller: <strong className="text-gray-900">{seller?.name ?? seller?.email}</strong>
                    </p>
                    {currentPlanId && (
                        <p className="text-xs text-gray-500 mt-1">
                            Current plan:{" "}
                            <span className="font-medium text-gray-900">
                                {seller?.pricing_plan?.name ?? `Plan #${currentPlanId}`}
                            </span>
                        </p>
                    )}

                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select a plan</label>
                        {loadingPlans ? (
                            <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading plans…
                            </div>
                        ) : plans.length === 0 ? (
                            <p className="text-sm text-gray-500">
                                No active plans available. Create one from the Pricing Plans page.
                            </p>
                        ) : (
                            <select
                                value={selectedPlanId}
                                onChange={(e) => setSelectedPlanId(e.target.value)}
                                disabled={submitting}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                <option value="">— Select a plan —</option>
                                {plans.map((plan) => (
                                    <option key={plan.id} value={plan.id}>
                                        {plan.name} — ${Number(plan.price).toFixed(2)} / {plan.plan_days} days
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50">
                    {currentPlanId && (
                        <button
                            onClick={handleRemove}
                            disabled={submitting}
                            className="inline-flex items-center px-4 py-2 border border-red-300 rounded-lg text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                        >
                            {submitting ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <XCircle className="h-4 w-4 mr-2" />
                            )}
                            Remove Plan
                        </button>
                    )}
                    <div className="flex-1" />
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleAssign}
                        disabled={
                            submitting ||
                            !selectedPlanId ||
                            String(selectedPlanId) === String(currentPlanId ?? "")
                        }
                        className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                        {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        {currentPlanId ? "Change Plan" : "Assign Plan"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignPlanDialog;
